<template>
    <div class="bk" :style="{ '--bk-cookies': cookiesAlto + 'px' }">
        <!-- ── Lateral: título, texto y la navegación de la cuenta ── -->
        <aside class="bk-side">
            <h1 class="bk-title">
                {{ $t('datos_bancarios.title_a') }} <span class="bk-grad">{{ $t('datos_bancarios.title_b') }}</span>
            </h1>
            <p class="bk-subtitle">{{ $t('datos_bancarios.subtitle') }}</p>
            <CuentaNav />
        </aside>

        <div class="bk-main">

        <!-- ── Cargando (solo si todavía no hay nada que mostrar) ── -->
        <div v-if="cargando" class="bk-loading">
            <HyFLoader variant="inline" />
        </div>

        <!-- ── Formulario ── -->
        <form v-else class="bk-form" novalidate @submit.prevent="guardar">

            <!-- 1 · Titular -->
            <section class="bk-card" :class="{ 'is-done': seccionCompleta(SECCIONES[0]) }" aria-labelledby="bk-s1">
                <header class="bk-card-head">
                    <span class="bk-step" aria-hidden="true">{{ seccionCompleta(SECCIONES[0]) ? '✓' : '1' }}</span>
                    <div>
                        <h2 id="bk-s1" class="bk-card-title">{{ $t('datos_bancarios.s1.title') }}</h2>
                        <p class="bk-card-desc">{{ $t('datos_bancarios.s1.desc') }}</p>
                    </div>
                </header>

                <div class="bk-grid-2">
                    <BkField id="bk-first_name" v-model="formData.first_name" :label="$t('datos_bancarios.f.first_name')"
                        autocomplete="given-name" :maxlength="30" :error="errorDe('first_name')" :ok="okDe('first_name')"
                        @blur="tocar('first_name')" />
                    <BkField id="bk-last_name" v-model="formData.last_name" :label="$t('datos_bancarios.f.last_name')"
                        autocomplete="family-name" :maxlength="40" :error="errorDe('last_name')" :ok="okDe('last_name')"
                        @blur="tocar('last_name')" />
                </div>

                <BkField id="bk-government_id" v-model="formData.government_id" :label="$t('datos_bancarios.f.government_id')"
                    :hint="$t('datos_bancarios.f.government_id_hint')" :maxlength="15" :error="errorDe('government_id')"
                    :ok="okDe('government_id')" @blur="tocar('government_id')" />

                <!-- Teléfono: código de país (con bandera y buscador, como en el
                     formulario nuevo) + número. Se guarda junto ("+54…"), como siempre. -->
                <div :class="['bk-phone', { 'is-invalid': !!errorDe('phone') }]">
                    <NfCombo
                        v-model="telPais"
                        class="bk-combo"
                        :options="opcionesPais"
                        :label="$t('datos_bancarios.f.country')"
                        :search-placeholder="$t('datos_bancarios.f.country_search')"
                        :empty-text="$t('datos_bancarios.f.country_empty')"
                        show-meta
                        @change="componerTelefono"
                    />
                    <BkField id="bk-phone" :value="telNumero" :label="$t('datos_bancarios.f.phone')" type="tel" inputmode="tel"
                        autocomplete="tel-national" optional :optional-text="$t('datos_bancarios.optional')"
                        :error="errorDe('phone')" :ok="okDe('phone')"
                        @input="v => { telNumero = v; componerTelefono() }" @blur="tocar('phone')" />
                </div>

                <BkField id="bk-email" v-model="formData.email" :label="$t('datos_bancarios.f.email')" type="email" inputmode="email"
                    autocomplete="email" optional :optional-text="$t('datos_bancarios.optional')"
                    :error="errorDe('email')" :ok="okDe('email')" @blur="tocar('email')" />
            </section>

            <!-- 2 · Cuenta -->
            <section class="bk-card" :class="{ 'is-done': seccionCompleta(SECCIONES[1]) }" aria-labelledby="bk-s2">
                <header class="bk-card-head">
                    <span class="bk-step" aria-hidden="true">{{ seccionCompleta(SECCIONES[1]) ? '✓' : '2' }}</span>
                    <div>
                        <h2 id="bk-s2" class="bk-card-title">{{ $t('datos_bancarios.s2.title') }}</h2>
                        <p class="bk-card-desc">{{ $t('datos_bancarios.s2.desc') }}</p>
                    </div>
                </header>

                <BkField id="bk-bank_account_number" v-model="formData.bank_account_number" :label="$t('datos_bancarios.f.account')"
                    inputmode="text" :error="errorDe('bank_account_number')" :ok="okDe('bank_account_number')"
                    @blur="tocar('bank_account_number')" />
                <BkField id="bk-bank_code" v-model="formData.bank_code" :label="$t('datos_bancarios.f.bank_code')"
                    :hint="$t('datos_bancarios.f.bank_code_hint')" :error="errorDe('bank_code')" :ok="okDe('bank_code')"
                    @blur="tocar('bank_code')" />
            </section>

            <!-- 3 · Banco -->
            <section class="bk-card" :class="{ 'is-done': seccionCompleta(SECCIONES[2]) }" aria-labelledby="bk-s3">
                <header class="bk-card-head">
                    <span class="bk-step" aria-hidden="true">{{ seccionCompleta(SECCIONES[2]) ? '✓' : '3' }}</span>
                    <div>
                        <h2 id="bk-s3" class="bk-card-title">{{ $t('datos_bancarios.s3.title') }}</h2>
                        <p class="bk-card-desc">{{ $t('datos_bancarios.s3.desc') }}</p>
                    </div>
                </header>

                <BkField id="bk-bank_name" v-model="formData.bank_name" :label="$t('datos_bancarios.f.bank_name')"
                    autocomplete="organization" :error="errorDe('bank_name')" :ok="okDe('bank_name')" @blur="tocar('bank_name')" />
                <BkField id="bk-bank_address" v-model="formData.bank_address" :label="$t('datos_bancarios.f.bank_address')"
                    optional :optional-text="$t('datos_bancarios.optional')" :maxlength="150"
                    :error="errorDe('bank_address')" :ok="okDe('bank_address')" @blur="tocar('bank_address')" />
                <BkField id="bk-branch_code" v-model="formData.branch_code" :label="$t('datos_bancarios.f.branch_code')"
                    optional :optional-text="$t('datos_bancarios.optional')" :maxlength="50"
                    :error="errorDe('branch_code')" :ok="okDe('branch_code')" @blur="tocar('branch_code')" />
                <BkField id="bk-message" v-model="formData.message" :label="$t('datos_bancarios.f.message')"
                    :hint="$t('datos_bancarios.f.message_hint')" optional :optional-text="$t('datos_bancarios.optional')"
                    textarea :maxlength="255" :error="errorDe('message')" @blur="tocar('message')" />
            </section>

            <!-- ── Barra de guardado (fija abajo) ── -->
            <div class="bk-bar">
            <!-- Aviso (éxito / error): sale justo arriba de la barra, alineado con el formulario. -->
            <transition name="bk-toast">
                <div
                    v-if="aviso"
                    :key="aviso.id"
                    :class="['bk-toast', `bk-toast--${aviso.tipo}`]"
                    :role="aviso.tipo === 'error' ? 'alert' : 'status'"
                    :aria-live="aviso.tipo === 'error' ? 'assertive' : 'polite'"
                >
                    <span class="bk-toast-icon" aria-hidden="true">{{ aviso.tipo === 'ok' ? '✓' : '!' }}</span>
                    <p class="bk-toast-text">{{ aviso.texto }}</p>
                    <button v-if="aviso.reintentar" type="button" class="bk-toast-retry" @click="aviso.reintentar">
                        {{ $t('datos_bancarios.toast.retry') }}
                    </button>
                    <button type="button" class="bk-toast-close" :aria-label="$t('datos_bancarios.toast.close')" @click="cerrarAviso">×</button>
                </div>
            </transition>
                <p class="bk-bar-state" :class="{ 'is-dirty': hayCambios }" aria-live="polite">
                    <span class="bk-bar-dot" aria-hidden="true"></span>
                    {{ hayCambios ? $t('datos_bancarios.state_unsaved') : $t('datos_bancarios.state_saved') }}
                </p>
                <div class="bk-bar-actions">
                    <button v-if="hayCambios" type="button" class="bk-btn bk-btn--ghost" :disabled="guardando" @click="descartar">
                        {{ $t('datos_bancarios.discard') }}
                    </button>
                    <button type="submit" class="bk-btn bk-btn--primary" :disabled="guardando || !hayCambios">
                        <span v-if="guardando" class="bk-spinner" aria-hidden="true"></span>
                        {{ guardando ? $t('datos_bancarios.saving') : $t('datos_bancarios.save') }}
                    </button>
                </div>
            </div>
        </form>
        </div><!-- /.bk-main -->

    </div>
</template>

<script>
import { mapState, mapActions } from "vuex";
import { validations } from "@/components/ValidatedFields/bankInformationRules";
import { PAISES_TELEFONO, nombrePais, separarTelefono } from "@/assets/data/paisesTelefono";
import NfCombo from "@/components/Formulario/NfCombo.vue";
import HyFLoader from "@/components/Loader/HyFLoader.vue";
import BkField from "@/components/BankInformation/BkField.vue";
import cuentaFormMixin from "@/components/BankInformation/cuentaFormMixin";
import CuentaNav from "@/components/Cuenta/CuentaNav.vue";

// Los campos que la API sabe guardar. Es también el orden en que se mandan:
// lo que no esté acá (id, user_id, created_at...) no viaja en el PUT.
const BANK_FIELDS = [
    'first_name',
    'last_name',
    'government_id',
    'phone',
    'email',
    'bank_account_number',
    'bank_code',
    'bank_name',
    'bank_address',
    'branch_code',
    'message',
];

// Campos de cada tarjeta (para el ✓ de la tarjeta). Las mismas reglas de
// validación de siempre (bankInformationRules.js) deciden qué es obligatorio.
const SECCIONES = [
    ['first_name', 'last_name', 'government_id', 'phone', 'email'],
    ['bank_account_number', 'bank_code'],
    ['bank_name', 'bank_address', 'branch_code', 'message'],
];


function emptyBankForm() {
    return BANK_FIELDS.reduce((form, field) => {
        form[field] = '';
        return form;
    }, {});
}

export default {
    name: "BankInformation",
    components: { HyFLoader, BkField, NfCombo, CuentaNav },
    mixins: [cuentaFormMixin],
    data() {
        return {
            SECCIONES,
            // Todos los campos declarados desde el arranque: en Vue 2 una
            // propiedad agregada después a un objeto vacío no es reactiva.
            formData: emptyBankForm(),
            // Lo último que se sabe guardado en la base (para "sin cambios" y
            // para descartar).
            guardado: emptyBankForm(),
            telPais: '',
            telNumero: '',
            cargando: true,
            guardando: false,
            intentoGuardar: false,
            tocados: {},
        };
    },
    validations: validations,
    watch: {
        // "Revisa los campos marcados en rojo" se va solo cuando ya no queda
        // ninguno en rojo.
        '$v.$invalid'(invalido) {
            if (!invalido && this.aviso && this.aviso.validacion) this.cerrarAviso();
        },
    },
    computed: {
        ...mapState("bankData", ["bankData"]),

        // Todos los países del mundo con prefijo (246), con bandera y nombre en
        // el idioma del sitio, ordenados por ese nombre. Primero "Sin código".
        opcionesPais() {
            const locale = this.$i18n.locale;
            const lista = PAISES_TELEFONO
                .map(p => ({ value: p.code, label: nombrePais(p.code, locale), meta: '+' + p.dial, flag: p.code }))
                .sort((a, b) => a.label.localeCompare(b.label, locale));
            return [{ value: '', label: this.$t('datos_bancarios.f.country_none'), meta: '', flag: '' }, ...lista];
        },
        hayCambios() {
            return BANK_FIELDS.some(f => (this.formData[f] || '') !== (this.guardado[f] || ''));
        },
    },
    methods: {
        ...mapActions("bankData", ["getBankData", "putBankData"]),

        // ── Validación ────────────────────────────────────────────────────
        tocar(campo) {
            this.$set(this.tocados, campo, true);
            this.$v.formData[campo].$touch();
        },
        // El error se muestra recién al salir del campo o al intentar guardar:
        // no se marca en rojo algo que la persona todavía está escribiendo.
        errorDe(campo) {
            const v = this.$v.formData[campo];
            if (!v || !v.$invalid || !(this.tocados[campo] || this.intentoGuardar)) return '';
            const regla = Object.keys(v.$params).find(r => !v[r]);
            return regla ? this.$t(`validations.${regla}`, v.$params[regla] || {}) : '';
        },
        okDe(campo) {
            const v = this.$v.formData[campo];
            return !!(this.formData[campo] && v && !v.$invalid);
        },
        seccionCompleta(campos) {
            return campos.every(c => !this.$v.formData[c].$invalid)
                && campos.some(c => this.formData[c]);
        },

        // ── Teléfono ──────────────────────────────────────────────────────
        // Separa "+54 11…" en país + número. Solo para mostrarlo: el valor
        // guardado no se toca hasta que la persona edite el teléfono.
        separarTelefono(tel) {
            const { pais, numero } = separarTelefono(tel);
            this.telPais = pais;
            this.telNumero = numero;
        },
        componerTelefono() {
            const pais = PAISES_TELEFONO.find(c => c.code === this.telPais);
            const numero = (this.telNumero || '').trim();
            this.formData.phone = pais ? `+${pais.dial}${numero}` : numero;
        },

        // ── Datos ─────────────────────────────────────────────────────────
        /**
         * Vuelca lo que devolvió la API. Los campos sin cargar vienen null y
         * se pasan a '': un null en un input se ve como "null".
         */
        applyBankData(data) {
            if (!data) return;
            BANK_FIELDS.forEach(f => {
                const v = data[f] === null || data[f] === undefined ? '' : String(data[f]);
                this.formData[f] = v;
                this.guardado[f] = v;
            });
            this.separarTelefono(this.formData.phone);
        },

        /** El mensaje que mandó la API, si mandó alguno. */
        apiMessage(error) {
            const data = error && error.response ? error.response.data : null;
            if (data && typeof data === 'object') {
                if (data.message) return data.message;
                // Errores de validación: { campo: ["mensaje", ...] }
                const first = Object.keys(data)
                    .map(key => (Array.isArray(data[key]) ? data[key][0] : null))
                    .find(Boolean);
                if (first) return first;
            }
            return null;
        },

        async cargar() {
            try {
                const data = await this.getBankData();
                // Si la persona ya empezó a escribir mientras cargaba, no se le
                // pisa lo escrito: solo se actualiza la referencia de "guardado".
                if (this.hayCambios && !this.cargando) {
                    BANK_FIELDS.forEach(f => { this.guardado[f] = data && data[f] != null ? String(data[f]) : ''; });
                } else {
                    this.applyBankData(data);
                }
            } catch (error) {
                this.mostrarAviso('error', this.$t('datos_bancarios.toast.error_load'), () => { this.cerrarAviso(); this.cargar(); });
            } finally {
                this.cargando = false;
            }
        },

        async guardar() {
            if (this.guardando) return;

            this.intentoGuardar = true;
            this.$v.$touch();
            if (this.$v.$invalid) {
                this.mostrarAviso('error', this.$t('datos_bancarios.toast.invalid'), null, { validacion: true });
                this.$nextTick(this.enfocarPrimerError);
                return;
            }

            this.guardando = true;
            try {
                const payload = BANK_FIELDS.reduce((data, f) => {
                    data[f] = (this.formData[f] || '').trim();
                    return data;
                }, {});

                const saved = await this.putBankData(payload);

                // Se repinta con lo que quedó en la base: si algo no se guardó,
                // se ve en el acto.
                this.applyBankData(saved || payload);
                this.intentoGuardar = false;
                this.tocados = {};
                this.$v.$reset();
                this.mostrarAviso('ok', this.$t('datos_bancarios.toast.saved'));
            } catch (error) {
                console.error('[BankInformation] error guardando los datos bancarios:', error);
                this.mostrarAviso(
                    'error',
                    this.apiMessage(error) || this.$t('datos_bancarios.toast.error_save'),
                    () => { this.cerrarAviso(); this.guardar(); }
                );
            } finally {
                this.guardando = false;
            }
        },

        descartar() {
            BANK_FIELDS.forEach(f => { this.formData[f] = this.guardado[f]; });
            this.separarTelefono(this.formData.phone);
            this.intentoGuardar = false;
            this.tocados = {};
            this.$v.$reset();
        },

        enfocarPrimerError() {
            const campo = BANK_FIELDS.find(f => this.$v.formData[f].$invalid);
            const el = campo && document.getElementById(`bk-${campo}`);
            if (!el) return;
            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            el.focus({ preventScroll: true });
        },

    },
    created() {
        // Si el store ya tiene los datos de una visita anterior se pintan de
        // entrada y el GET los confirma. El estado inicial del store es un
        // objeto vacío: eso NO cuenta como datos (se mostraba el formulario en
        // blanco sin el loader mientras el GET seguía en camino).
        if (this.bankData && this.bankData.id) {
            this.applyBankData(this.bankData);
            this.cargando = false;
        }
        this.cargar();
    },
};
</script>

<!-- Estilos compartidos con Editar perfil (todo con prefijo bk-). -->
<style src="../../components/BankInformation/bkCuenta.css"></style>
<style src="../../components/Cuenta/cuentaMenu.css"></style>
